'use client';

import {
  type AdultWarningOptions,
  type ConfirmCancelOptions,
  type ConfirmOptions,
  ModalAdapterProvider,
  type RecaptchaOptions,
  type SharedModalAdapter,
  type StoreOptions,
} from '@module/helpers/modal/adapter';
import useModalStore from '@module/stores/useModalStore';
import { MODAL_TYPE_PC } from '@module/types/modal/type';
import type { ReactNode } from 'react';

/**
 * pandatv-pc 어댑터 = 카탈로그(`SharedModalAdapter`)에서 PC가 쓰는 메서드만 추출.
 */
export type PandatvPcModalAdapter = Required<
  Pick<
    SharedModalAdapter,
    | 'showConfirm'
    | 'showConfirmCancel'
    | 'showPasswordEntry'
    | 'showRecaptcha'
    | 'clear'
    | 'pop'
    | 'showAuth'
    | 'showIdentityVerification'
    | 'showAdultWarning'
    | 'showStore'
  >
>;

/**
 * useModalStore를 ModalAdapter 인터페이스로 래핑하는 어댑터
 * 콜백 기반 모달을 Promise 기반으로 변환
 */
function createZustandModalAdapter(): PandatvPcModalAdapter {
  const getStore = () => useModalStore.getState();

  return {
    showConfirm: (options: ConfirmOptions) => {
      return new Promise<boolean>((resolve) => {
        getStore().pushModal({
          type: MODAL_TYPE_PC.CONFIRM_MODAL,
          id: 'ZustandAdapter-Confirm',
          options: {
            description: options.description,
            onClose: () => {
              options.onClose?.();
              getStore().popModal();
              resolve(true);
            },
          },
        });
      });
    },

    showConfirmCancel: (options: ConfirmCancelOptions) => {
      return new Promise<boolean>((resolve) => {
        getStore().pushModal({
          type: MODAL_TYPE_PC.CONFIRM_CANCEL_MODAL,
          id: 'ZustandAdapter-ConfirmCancel',
          options: {
            description: options.description,
            rightText: options.confirmText,
            leftText: options.cancelText,
            onClick: () => {
              getStore().popModal();
              resolve(true);
            },
            onClose: () => {
              getStore().popModal();
              resolve(false);
            },
          },
        });
      });
    },

    showAuth: () => {
      return new Promise<boolean>((resolve) => {
        getStore().pushModal({
          type: MODAL_TYPE_PC.AUTH_MODAL,
          id: 'ZustandAdapter-Auth',
          options: {
            onSuccess: () => {
              getStore().popModal();
              resolve(true);
            },
            onClose: () => {
              getStore().popModal();
              resolve(false);
            },
          },
        });
      });
    },

    showIdentityVerification: () => {
      return new Promise<boolean>((resolve) => {
        getStore().pushModal({
          type: MODAL_TYPE_PC.IDENTITY_VERIFICATION_MODAL,
          id: 'ZustandAdapter-IdentityVerification',
          options: {
            onSuccess: () => {
              getStore().popModal();
              resolve(true);
            },
            onClose: () => {
              getStore().popModal();
              resolve(false);
            },
          },
        });
      });
    },

    showAdultWarning: (options: AdultWarningOptions) => {
      return new Promise<boolean>((resolve) => {
        getStore().pushModal({
          type: MODAL_TYPE_PC.ADULT_WARNING_MODAL,
          id: 'ZustandAdapter-AdultWarning',
          options: {
            media: options.media,
            onConfirm: () => {
              getStore().popModal();
              resolve(true);
            },
            onClose: () => {
              getStore().popModal();
              resolve(false);
            },
          },
        });
      });
    },

    showPasswordEntry: () => {
      return new Promise<{ status: 'submit' | 'cancel'; password?: string }>((resolve) => {
        getStore().pushModal({
          type: MODAL_TYPE_PC.PASSWORD_ENTRY_MODAL,
          id: 'ZustandAdapter-PasswordEntry',
          options: {
            onClick: (password: string) => {
              getStore().popModal();
              resolve({ status: 'submit', password });
            },
            onClose: () => {
              getStore().popModal();
              resolve({ status: 'cancel' });
            },
          },
        });
      });
    },

    showRecaptcha: (options: RecaptchaOptions) => {
      return new Promise<string | null>((resolve) => {
        getStore().pushModal({
          type: MODAL_TYPE_PC.RECAPTCHA_MODAL,
          id: 'ZustandAdapter-Recaptcha',
          options: {
            description: options.description,
            onChange: (token: string) => {
              if (token) {
                getStore().popModal();
                resolve(token);
              }
            },
            onClose: () => {
              getStore().popModal();
              resolve(null);
            },
          },
        });
      });
    },

    showStore: (options: StoreOptions) => {
      return new Promise<void>((resolve) => {
        getStore().pushModal({
          type: MODAL_TYPE_PC.STORE_MODAL,
          options: {
            tab: options.tab,
            onClose: () => {
              getStore().popModal();
              resolve();
            },
          },
        });
      });
    },

    clear: () => {
      getStore().clearModals();
    },

    pop: () => {
      getStore().popModal();
    },
  };
}

// 싱글톤 어댑터 인스턴스
const zustandModalAdapter = createZustandModalAdapter();

interface ZustandModalAdapterProviderProps {
  children: ReactNode;
}

export function ZustandModalAdapterProvider({ children }: ZustandModalAdapterProviderProps) {
  return <ModalAdapterProvider adapter={zustandModalAdapter}>{children}</ModalAdapterProvider>;
}
